import type {
  BarrierVariant,
  DialogueId,
  EncounterId,
  EntityTypeId,
  GeneratorId,
  LevelDocument,
  LevelEnvironmentDocument,
  PickupKind,
  TileCatalogDocument,
  TileDocument,
} from "../content/types";
import { Point } from "../runtime";

export interface Obstacle {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

export interface TriggerPass {
  xx: number;
  yy: number;
  encounter?: EncounterId;
  dialogue?: DialogueId;
}

export interface Barrier {
  xx: number;
  yy: number;
  id: number;
  encounter: EncounterId;
  isOn: boolean;
  vanishCounter: number;
  vanishRate: number;
  vanished: boolean;
}

export interface Block {
  xx: number;
  yy: number;
  id: number;
}

export interface EnemySpawn {
  xx: number;
  yy: number;
  entity: EntityTypeId;
  count: number;
}

export interface ItemPickup {
  xx: number;
  yy: number;
  kind: PickupKind;
}

export interface GeneratorTarget {
  id: GeneratorId;
  xx: number;
  yy: number;
}

export interface RuntimeEncounter {
  readonly id: EncounterId;
  readonly spawns: readonly EnemySpawn[];
  readonly activateOnClear?: EncounterId;
  readonly dialogueOnClear?: DialogueId;
}

export interface RuntimeBoss {
  readonly xx: number;
  readonly yy: number;
  readonly entity: EntityTypeId;
  readonly variant: string;
  readonly lowHealthDialogue?: DialogueId;
  readonly defeatedDialogue?: DialogueId;
}

export interface RuntimeSurvival {
  readonly loop: readonly EncounterId[];
  readonly indexByEncounter: ReadonlyMap<EncounterId, number>;
  readonly harderAfterWave: number;
}

export interface Level {
  readonly id: LevelDocument["id"];
  readonly document: LevelDocument;
  readonly zone: LevelEnvironmentDocument["zone"];
  readonly bgX: number;
  readonly bgY: number;
  readonly raining: number;
  readonly fortressStage: 0 | 1 | 2 | 3;
  readonly music: string;
  readonly bgArray: readonly (number | null)[];
  readonly obstacleArray: Obstacle[][];
  readonly triggerPass: TriggerPass[];
  readonly barriers: Barrier[];
  readonly blocks: Block[];
  readonly encounters: readonly RuntimeEncounter[];
  readonly encounterById: ReadonlyMap<EncounterId, RuntimeEncounter>;
  readonly itemPickup: ItemPickup[];
  readonly generators: GeneratorTarget[];
  readonly startPoint: Point;
  readonly bossData: RuntimeBoss | null;
  readonly survival: RuntimeSurvival | null;
}

const BARRIER_IDS: Readonly<Record<BarrierVariant, number>> = {
  "horizontal-start": 0,
  "horizontal-end": 1,
  "horizontal-middle": 2,
  "vertical-start": 3,
  "vertical-end": 4,
  "vertical-middle": 5,
};

export function buildLevel(
  document: LevelDocument,
  tileCatalog: TileCatalogDocument,
): Level {
  const tiles = document.environment.tiles;
  const bgY = tiles.length;
  const bgX = tiles[0]?.length ?? 0;
  const bgArray = tiles.flat();
  const obstacleArray = buildObstacles(bgArray, bgX, tileCatalog);
  const encounters = document.encounters.map((encounter) => ({
    id: encounter.id,
    spawns: (encounter.spawns ?? []).map((spawn) => ({
      xx: spawn.at.x * tileCatalog.cellSize,
      yy: spawn.at.y * tileCatalog.cellSize,
      entity: spawn.entity,
      count: spawn.count ?? 1,
    })),
    activateOnClear: encounter.onClear?.activate,
    dialogueOnClear: encounter.onClear?.dialogue,
  }));
  const encounterById = new Map(
    encounters.map((encounter) => [encounter.id, encounter] as const),
  );
  const barriers: Barrier[] = [];
  for (const encounter of document.encounters) {
    for (const barrier of encounter.barriers ?? []) {
      barriers.push({
        xx: barrier.at.x * tileCatalog.cellSize,
        yy: barrier.at.y * tileCatalog.cellSize,
        id: BARRIER_IDS[barrier.variant],
        encounter: encounter.id,
        isOn: false,
        vanishCounter: 0,
        vanishRate: 4,
        vanished: true,
      });
    }
  }
  const blocks = (document.blocks ?? []).map((block) => ({
    xx: block.at.x * tileCatalog.cellSize,
    yy: block.at.y * tileCatalog.cellSize,
    id: block.variant === "a" ? 0 : 1,
  }));
  for (const block of blocks) {
    obstacleArray[
      tileIndex(block.xx, block.yy, bgX, tileCatalog.tileSize)
    ].push({
      x1: block.xx,
      x2: block.xx + tileCatalog.cellSize,
      y1: block.yy,
      y2: block.yy + tileCatalog.cellSize,
    });
  }
  const generators = (document.generators ?? []).map((generator) => ({
    id: generator.id,
    xx: generator.at.x * tileCatalog.cellSize,
    yy: generator.at.y * tileCatalog.cellSize,
  }));
  const boss = document.boss;
  const bossData = boss
    ? {
        xx: boss.at.x * tileCatalog.cellSize,
        yy: boss.at.y * tileCatalog.cellSize,
        entity: boss.entity,
        variant: boss.variant,
        lowHealthDialogue: boss.lowHealthDialogue,
        defeatedDialogue: boss.defeatedDialogue,
      }
    : null;
  for (const rectangle of document.obstacles ?? []) {
    const x1 = rectangle.from.x * tileCatalog.cellSize;
    const y1 = rectangle.from.y * tileCatalog.cellSize;
    const bucket =
      rectangle.bucket === undefined
        ? tileIndex(x1, y1, bgX, tileCatalog.tileSize)
        : rectangle.bucket.x + rectangle.bucket.y * bgX;
    obstacleArray[bucket].push({
      x1,
      x2: rectangle.to.x * tileCatalog.cellSize,
      y1,
      y2: rectangle.to.y * tileCatalog.cellSize,
    });
  }
  const loop = document.survival?.loop;
  return {
    id: document.id,
    document,
    zone: document.environment.zone,
    bgX,
    bgY,
    raining: document.environment.rain ? 1 : 0,
    fortressStage: document.environment.fortressStage ?? 0,
    music: document.environment.music,
    bgArray,
    obstacleArray,
    triggerPass: document.triggers.map((trigger) => ({
      xx: trigger.at.x * tileCatalog.cellSize,
      yy: trigger.at.y * tileCatalog.cellSize,
      encounter: trigger.encounter,
      dialogue: trigger.dialogue,
    })),
    barriers,
    blocks,
    encounters,
    encounterById,
    itemPickup: (document.pickups ?? []).map((pickup) => ({
      xx: pickup.at.x * tileCatalog.cellSize,
      yy: pickup.at.y * tileCatalog.cellSize,
      kind: pickup.kind,
    })),
    generators,
    startPoint: new Point(
      document.start.x * tileCatalog.cellSize,
      document.start.y * tileCatalog.cellSize,
    ),
    bossData,
    survival: loop
      ? {
          loop,
          indexByEncounter: new Map(
            loop.map((encounterId, index) => [encounterId, index] as const),
          ),
          harderAfterWave: document.survival!.harderAfterWave,
        }
      : null,
  };
}

function buildObstacles(
  tiles: readonly (number | null)[],
  backgroundColumns: number,
  catalog: TileCatalogDocument,
): Obstacle[][] {
  const tileById = new Map<number, TileDocument>(
    catalog.tiles.map((tile) => [tile.id, tile]),
  );
  return tiles.map((id, bg) => {
    if (id === null) return [];
    const ox = (bg % backgroundColumns) * catalog.tileSize;
    const oy = Math.floor(bg / backgroundColumns) * catalog.tileSize;
    const tile = tileById.get(id);
    if (!tile) throw new Error(`unknown background tile '${id}'`);
    return tile.obstacles.map(({ from, to }) => ({
      x1: from.x * catalog.cellSize + ox,
      x2: to.x * catalog.cellSize + ox,
      y1: from.y * catalog.cellSize + oy,
      y2: to.y * catalog.cellSize + oy,
    }));
  });
}

function tileIndex(
  x: number,
  y: number,
  backgroundColumns: number,
  tileSize: number,
): number {
  return (
    Math.floor(x / tileSize) + Math.floor(y / tileSize) * backgroundColumns
  );
}
